import React, { useState, useEffect } from "react";
import Image from "@/components/common/Image";
import { ChevronLeft, ChevronRight } from "lucide-react"; // Import icons for pagination

interface Tag {
  slug: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

interface Integration {
  slug: string;
  name: string;
  icon: string;
  type: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

const API_BASE_URL =
  process.env.REACT_APP_CMS_BASE_URL || "http://localhost:3000";

export const Integrations: React.FC = () => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [selectedTag, setSelectedTag] = useState<string>("enterprise");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState<{ [key: string]: number }>({});
  const [tagCounts, setTagCounts] = useState<{ [key: string]: number }>({});
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const itemsPerPage = 20; // 4 columns x 5 rows

  // Available filter options
  const filterOptions = ["Platform","Enterprise","Auth Only", ];

  // Display names for filter options
  const filterDisplayNames: Record<string, string> = {
    "Platform": "MCP Server"
  };

  // Get display name for a filter option
  const getFilterDisplayName = (option: string) => {
    return filterDisplayNames[option] || option;
  };

  // Check if filters should be disabled (when Featured tab is selected)
  const areFiltersDisabled = selectedTag === "enterprise";

  // Fetch tags and integrations
  useEffect(() => {
    // Fetch tags
    fetch(`${API_BASE_URL}/api/tags?pageSize=0`)
      .then((res) => res.json())
      .then((data) => {
        setTags(data.data);
      })
      .catch((error) => console.error("Error fetching tags:", error));

    // Fetch integrations
    fetch(`${API_BASE_URL}/api/integrations?pageSize=0`)
      .then((res) => res.json())
      .then((data) => {
        setIntegrations(data.data);
      })
      .catch((error) => console.error("Error fetching integrations:", error));
  }, []);

  // Calculate tag counts whenever tags or integrations change
  useEffect(() => {
    if (tags.length > 0 && integrations.length > 0) {
      const counts: { [key: string]: number } = {};

      // Initialize counts for all tags
      tags.forEach((tag) => {
        counts[tag.slug] = 0;
      });

      // Count integrations for each tag
      integrations.forEach((integration) => {
        integration.tags.forEach((tagSlug) => {
          if (counts[tagSlug] !== undefined) {
            counts[tagSlug]++;
          }
        });
      });

      // Add count for "All Integrations" tab
      counts["all"] = integrations.length;

      // Add count for "Enterprise" tab
      counts["enterprise"] = integrations.filter(integration =>
        integration.type === "Enterprise"
      ).length;

      setTagCounts(counts);
    }
  }, [tags, integrations]);

  // Toggle filter selection
  const toggleFilter = (option: string) => {
    setSelectedFilters(prev => {
      if (prev.includes(option)) {
        return prev.filter(f => f !== option);
      } else {
        return [...prev, option];
      }
    });
  };

  // Filter integrations based on search query, selected tag, and filters
  const filteredIntegrations = integrations.filter((integration) => {
    const matchesSearch = integration.name
      .toLowerCase()
      .includes(searchQuery.toLowerCase());

    // Handle the special "enterprise" tag
    if (selectedTag === "enterprise") {
      return matchesSearch && integration.type === "Enterprise" &&
        (selectedFilters.length === 0 || selectedFilters.some(filter => {
          if (filter === "Auth Only") {
            return integration.type === "Auth Only";
          }
          if (filter === "Platform") {
            return integration.type === "Platform";
          }
          if (filter === "Enterprise") {
            return integration.type === "Enterprise";
          }
          return false;
        }));
    }

    const matchesTag =
      selectedTag === "all" || integration.tags.includes(selectedTag);

    // Check if integration matches selected filters
    const matchesFilters = selectedFilters.length === 0 ||
      selectedFilters.some(filter => {
        if (filter === "Auth Only") {
          // Logic for Auth Only filter - adjust as needed
          return integration.type === "Auth Only";
        }
        if (filter === "Platform") {
          return integration.type === "Platform";
        }
        if (filter === "Enterprise") {
          return integration.type === "Enterprise";
        }
        return false;
      });

    return matchesSearch && matchesTag && matchesFilters;
  });

  // Paginate filtered integrations
  const currentPageForTag = currentPage[selectedTag] || 1;
  const startIndex = (currentPageForTag - 1) * itemsPerPage;
  const paginatedIntegrations = filteredIntegrations.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Reset to first page when changing filters
  useEffect(() => {
    setCurrentPage((prev) => ({ ...prev, [selectedTag]: 1 }));
  }, [selectedTag, searchQuery, selectedFilters]);



  // Handle page change for the selected tab
  const handlePageChange = (page: number) => {
    setCurrentPage((prev) => ({ ...prev, [selectedTag]: page }));
  };

  // Calculate total pages for the current tab
  const totalPages = Math.ceil(filteredIntegrations.length / itemsPerPage);

  // Handle click outside filter dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.filter-dropdown') && !target.closest('.filter-button')) {
        setShowFilterDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <section className="bg-white mb-4">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Desktop layout */}
        <div className="hidden md:flex gap-8">
          {/* Left sidebar with search and categories */}
          <div className="w-72 flex-shrink-0">
            <div className="sticky top-20">
              {/* Search Bar and Filter */}
              <div className="mb-4 flex gap-2">
                <div className="relative grow">
                  <input
                    type="text"
                    placeholder="Search..."
                    className="w-full pl-10 pr-4 py-2 bg-white border border-gray-400 rounded-lg focus:outline-none"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                    <svg
                      className="w-4 h-4 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                </div>

                {/* Filter Button */}
                <div className="relative group">
                  <button
                    onClick={() => !areFiltersDisabled && setShowFilterDropdown(!showFilterDropdown)}
                    className={`filter-button h-full flex items-center px-4 py-2 bg-white border border-gray-400 rounded-lg focus:outline-none ${
                      areFiltersDisabled ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    <span className="mr-2 text-sm text-gray-500">Filter</span>
                    <svg
                      className="w-5 h-5 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                      />
                    </svg>

                    {/* Tooltip for disabled filters */}
                    {areFiltersDisabled && (
                      <div className="absolute -bottom-16 right-0 w-64 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-50">
                        Filters are available when viewing "All Categories".
                      </div>
                    )}
                  </button>

                  {/* Filter Dropdown */}
                  {showFilterDropdown && !areFiltersDisabled && (
                    <div className="filter-dropdown absolute right-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-2">
                      {filterOptions.map((option, index) => (
                        <div
                          key={option + index}
                          onClick={() => toggleFilter(option)}
                          className={`px-6 py-3 cursor-pointer group flex items-start rounded-lg transition-colors ${
                            selectedFilters.includes(option)
                              ? "bg-primary-50 border border-primary-400 text-primary-600"
                              : "hover:bg-primary-50 hover:text-primary-600"
                          }`}
                        >
                          {selectedFilters.includes(option) && (
                            <svg className="w-5 h-5 mr-2 text-primary-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                          <div className={`${selectedFilters.includes(option) ? "" : "ml-7"}`}>
                            <div className="font-medium text-gray-900">
                              {getFilterDisplayName(option)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Tabs */}
              <div className="space-y-1 whitespace-nowrap">
                <button
                  className={`w-full text-left py-2 px-3 rounded-lg flex justify-between items-center ${
                    selectedTag === "enterprise"
                      ? "bg-primary-50 border border-primary-400 text-primary-600"
                      : "hover:bg-gray-50 border border-gray-400"
                  }`}
                  onClick={() => setSelectedTag("enterprise")}
                >
                  <span className="text-sm font-medium">Featured</span>
                  <span className="text-xs text-gray-500">
                    {tagCounts["enterprise"] || 0}
                  </span>
                </button>
                <button
                  className={`w-full text-left py-2 px-3 rounded-lg flex justify-between items-center ${
                    selectedTag === "all"
                      ? "bg-primary-50 border border-primary-400 text-primary-600"
                      : "hover:bg-gray-50 border border-gray-400"
                  }`}
                  onClick={() => setSelectedTag("all")}
                >
                  <span className="text-sm font-medium">All Categories</span>
                  <span className="text-xs text-gray-500">
                    {tagCounts["all"] || 0}
                  </span>
                </button>
                {tags.map((tag) => (
                  <button
                    key={tag.slug}
                    className={`w-full text-left py-2 px-3 rounded-lg flex justify-between items-center ${
                      selectedTag === tag.slug
                        ? "bg-primary-50 border border-primary-400 text-primary-600"
                        : "hover:bg-gray-50 border border-gray-400"
                    }`}
                    onClick={() => setSelectedTag(tag.slug)}
                  >
                    <span className="text-sm">{tag.name}</span>
                    <span className="text-xs text-gray-500">
                      {tagCounts[tag.slug] || 0}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right side with integration grid */}
          <div className="flex-1 min-w-0">
            {/* Active filters display */}
            {selectedFilters.length > 0 && (
              <div className="mb-4 flex flex-wrap gap-2">
                {selectedFilters.map(filter => (
                  <div key={filter} className="px-3 py-1 bg-primary-50 rounded-full flex items-center">
                    <span className="text-sm text-primary-600">{getFilterDisplayName(filter)}</span>
                    <button
                      onClick={() => toggleFilter(filter)}
                      className="ml-2 text-primary-600 hover:text-primary-800"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {paginatedIntegrations.map((integration) => {
                return (
                  <div
                    key={integration.slug}
                    className="flex flex-col items-center justify-center place-self-start relative w-full h-44 p-5 gap-5 border rounded-lg bg-white border-primary-100 transition-colors"
                  >
                    <div className="w-12 h-12 flex items-center justify-center">
                      <Image
                        src={integration.icon}
                        alt={integration.name}
                        className="w-10 h-10 object-contain"
                      />
                    </div>
                    <div className="flex flex-col text-center items-center">
                      <h3 className="text-base font-medium text-gray-900 mb-1">
                        {integration.name}
                      </h3>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination Controls */}
            <div className="flex justify-center mt-8 items-center gap-2">
              <button
                className="p-2 rounded-lg hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 hover:text-primary-600 transition-colors"
                onClick={() => handlePageChange(currentPageForTag - 1)}
                disabled={currentPageForTag === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-700">
                Page {currentPageForTag} of {totalPages}
              </span>
              <button
                className="p-2 rounded-lg hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 hover:text-primary-600 transition-colors"
                onClick={() => handlePageChange(currentPageForTag + 1)}
                disabled={currentPageForTag === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile layout */}
        <div className="md:hidden">
          {/* Search and Filter */}
          <div className="mb-4 flex gap-2">
            <div className="relative grow">
              <input
                type="text"
                placeholder="Search..."
                className="w-full pl-10 pr-4 py-2 bg-white border border-gray-400 rounded-lg focus:outline-none"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                <svg
                  className="w-4 h-4 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            {/* Filter Button */}
            <div className="relative group">
              <button
                onClick={() => !areFiltersDisabled && setShowFilterDropdown(!showFilterDropdown)}
                className={`filter-button h-full flex items-center px-4 py-2 bg-white border border-gray-400 rounded-lg focus:outline-none ${
                  areFiltersDisabled ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                  />
                </svg>
              </button>

              {/* Tooltip for disabled filters */}
              {areFiltersDisabled && (
                <div className="absolute -bottom-16 right-0 w-64 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-50">
                  Filters are available when viewing "All Categories".
                </div>
              )}

              {/* Filter Dropdown */}
              {showFilterDropdown && !areFiltersDisabled && (
                <div className="filter-dropdown absolute right-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-2">
                  {filterOptions.map((option, index) => (
                    <div
                      key={option + index}
                      onClick={() => toggleFilter(option)}
                      className={`px-6 py-3 cursor-pointer group flex items-start rounded-lg transition-colors ${
                        selectedFilters.includes(option)
                          ? "bg-primary-50 border border-primary-400 text-primary-600"
                          : "hover:bg-primary-50 hover:text-primary-600"
                      }`}
                    >
                      {selectedFilters.includes(option) && (
                        <svg className="w-5 h-5 mr-2 text-primary-600" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      <div className={`${selectedFilters.includes(option) ? "" : "ml-7"}`}>
                        <div className="font-medium text-gray-900">
                          {getFilterDisplayName(option)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Category Dropdown */}
          <div className="mb-4">
            <div className="relative">
              <select
                className="w-full py-2 pl-3 pr-8 bg-white border border-gray-400 rounded-lg focus:outline-none appearance-none"
                value={selectedTag}
                onChange={(e) => setSelectedTag(e.target.value)}
              >
                <option value="enterprise">
                  Featured ({tagCounts["enterprise"] || 0})
                </option>
                <option value="all">
                  All Categories ({tagCounts["all"] || 0})
                </option>
                {tags.map((tag) => (
                  <option key={tag.slug} value={tag.slug}>
                    {tag.name} ({tagCounts[tag.slug] || 0})
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg
                  className="w-4 h-4 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Active filters display */}
          {selectedFilters.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2">
              {selectedFilters.map(filter => (
                <div key={filter} className="px-3 py-1 bg-primary-50 rounded-full flex items-center">
                  <span className="text-sm text-primary-600">{getFilterDisplayName(filter)}</span>
                  <button
                    onClick={() => toggleFilter(filter)}
                    className="ml-2 text-primary-600 hover:text-primary-800"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Integration Grid - 3 columns on mobile */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {paginatedIntegrations.map((integration) => (
              <div
                key={integration.slug}
                className="flex flex-col items-center justify-center place-self-start relative w-full h-36 p-3 gap-2 border rounded-lg bg-white border-primary-100 transition-colors"
              >
                <div className="w-10 h-10 flex items-center justify-center">
                  <Image
                    src={integration.icon}
                    alt={integration.name}
                    className="w-8 h-8 object-contain"
                  />
                </div>
                <div className="flex flex-col text-center items-center">
                  <h3 className="text-sm font-medium text-gray-900 mb-1 line-clamp-1">
                    {integration.name}
                  </h3>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          <div className="flex justify-center mt-6 items-center gap-2">
            <button
              className="p-2 rounded-lg hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 hover:text-primary-600 transition-colors"
              onClick={() => handlePageChange(currentPageForTag - 1)}
              disabled={currentPageForTag === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-gray-700">
              Page {currentPageForTag} of {totalPages}
            </span>
            <button
              className="p-2 rounded-lg hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 hover:text-primary-600 transition-colors"
              onClick={() => handlePageChange(currentPageForTag + 1)}
              disabled={currentPageForTag === totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
